/**
 * The phone tab bar's destinations (Phase 3; decision note in report -102, approved by
 * Paul 2026-10-08; mobile's mapping matches, agreed in writing). Data only, so it can be
 * tested and the component file exports components alone.
 */
import {
  ArrowLeftRight,
  BookOpen,
  HelpCircle,
  Home,
  Landmark,
  PiggyBank,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import {
  MORE_BANKS_COPY,
  MORE_HELP_COPY,
  MORE_RULES_COPY,
  TAB_BUDGET_COPY,
  TAB_FORECAST_COPY,
  TAB_HOME_COPY,
  TAB_TRANSACTIONS_COPY,
} from "@fomofi/shared";
import { NAV_ITEMS } from "@/config/navItems";

interface Tab {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Paths that light this tab (the first is `to`). */
  lights: string[];
}

export const PHONE_TABS: Tab[] = [
  { to: "/dashboard", label: TAB_HOME_COPY, icon: Home, lights: ["/dashboard"] },
  { to: "/budgets", label: TAB_BUDGET_COPY, icon: PiggyBank, lights: ["/budgets"] },
  {
    to: "/transactions",
    label: TAB_TRANSACTIONS_COPY,
    icon: ArrowLeftRight,
    lights: ["/transactions"],
  },
  // /paycheck is reached from Forecast's header, so it lights Forecast.
  {
    to: "/forecast",
    label: TAB_FORECAST_COPY,
    icon: TrendingUp,
    lights: ["/forecast", "/paycheck"],
  },
];

const TAB_PATHS = new Set(PHONE_TABS.flatMap((t) => t.lights));
/** Payment and Settings sit after Banks and Rules in More (the approved order). */
const TAIL = new Set(["/payment", "/settings"]);

/** More: every drawer item that is not a tab, in the drawer's order, then the pages the
 *  drawer never listed (reached today from the profile menu and page links). */
export const MORE_ITEMS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  ...NAV_ITEMS.filter((item) => !TAB_PATHS.has(item.to) && !TAIL.has(item.to)),
  { to: "/banks", label: MORE_BANKS_COPY, icon: Landmark },
  { to: "/rules", label: MORE_RULES_COPY, icon: BookOpen },
  ...NAV_ITEMS.filter((item) => item.to === "/payment"),
  ...NAV_ITEMS.filter((item) => item.to === "/settings"),
  { to: "/help", label: MORE_HELP_COPY, icon: HelpCircle },
];

export function lit(pathname: string, paths: string[]): boolean {
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** More is lit on any page it lists, when no tab is. */
export function moreIsLit(pathname: string): boolean {
  return (
    !PHONE_TABS.some((t) => lit(pathname, t.lights)) &&
    MORE_ITEMS.some((m) => lit(pathname, [m.to]))
  );
}
